import { useEffect, useMemo, useRef, useState } from 'react'
import { AnchoredOverlay, EmptyState, LoadingState } from '@orbind/ui'
import type { GifDoGiphy } from '@orbind/core'
import { useGifs } from './useChat.ts'

/**
 * Seletor de emoji e GIF do compositor, no molde do mensageiro de referência:
 * abas de categoria em cima, busca, grade rolável e, embaixo, a troca entre
 * Emoji e GIF.
 *
 * Os emojis são os do SISTEMA (no Windows, os do Segoe UI Emoji): a lista vem
 * do emojibase em pt-BR — nomes e palavras-chave para a busca — e é carregada
 * só quando o seletor abre, para não pesar no app inteiro.
 */

type Emoji = { emoji: string; label: string; tags: string[]; version: number; group: number }

/** Categorias na ordem do mensageiro de referência (pessoas junto dos rostos). */
const CATEGORIAS: readonly { id: string; titulo: string; icone: string; grupos: number[] }[] = [
  { id: 'rostos', titulo: 'Smileys e pessoas', icone: 'far fa-smile', grupos: [0, 1] },
  { id: 'natureza', titulo: 'Animais e natureza', icone: 'fas fa-paw', grupos: [3] },
  { id: 'comida', titulo: 'Comidas e bebidas', icone: 'fas fa-coffee', grupos: [4] },
  { id: 'atividades', titulo: 'Atividades', icone: 'far fa-futbol', grupos: [6] },
  { id: 'viagens', titulo: 'Viagens e lugares', icone: 'fas fa-car', grupos: [5] },
  { id: 'objetos', titulo: 'Objetos', icone: 'far fa-lightbulb', grupos: [7] },
  { id: 'simbolos', titulo: 'Símbolos', icone: 'fas fa-icons', grupos: [8] },
  { id: 'bandeiras', titulo: 'Bandeiras', icone: 'far fa-flag', grupos: [9] },
]

const CHAVE_RECENTES = 'orbind.chat.emojisRecentes'
const MAX_RECENTES = 24

function lerRecentes(): string[] {
  try {
    const bruto = JSON.parse(localStorage.getItem(CHAVE_RECENTES) ?? '[]')
    return Array.isArray(bruto) ? bruto.filter((e): e is string => typeof e === 'string') : []
  } catch {
    return []
  }
}

function guardarRecente(emoji: string) {
  try {
    const lista = [emoji, ...lerRecentes().filter((e) => e !== emoji)].slice(0, MAX_RECENTES)
    localStorage.setItem(CHAVE_RECENTES, JSON.stringify(lista))
  } catch {
    // Armazenamento bloqueado: recentes só não persistem.
  }
}

/**
 * O sistema desenha este emoji?
 *
 * Emoji novo demais (o Windows 10 parou no Unicode 12) vira quadrado vazio ou
 * se desmonta em dois. Emoji colorido ignora a cor do texto; o quadrado de
 * "sem glifo" sai na cor pedida — e uma sequência desmontada fica larga. Só os
 * de versão recente passam pelo teste; os antigos todo sistema tem.
 */
let canvasDeTeste: CanvasRenderingContext2D | null | undefined
const larguraBase = { valor: 0 }
function sistemaDesenha(emoji: string): boolean {
  if (canvasDeTeste === undefined) {
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = 40
    canvasDeTeste = canvas.getContext('2d', { willReadFrequently: true })
    if (canvasDeTeste) {
      canvasDeTeste.font = '28px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif'
      canvasDeTeste.textBaseline = 'top'
      larguraBase.valor = canvasDeTeste.measureText('😀').width
    }
  }
  const ctx = canvasDeTeste
  if (!ctx) return true
  if (ctx.measureText(emoji).width > larguraBase.valor * 1.4) return false
  ctx.clearRect(0, 0, 40, 40)
  ctx.fillStyle = '#ff0000'
  ctx.fillText(emoji, 0, 4)
  const pixels = ctx.getImageData(0, 0, 40, 40).data
  for (let i = 0; i < pixels.length; i += 4) {
    const [r, g, b, a] = [pixels[i]!, pixels[i + 1]!, pixels[i + 2]!, pixels[i + 3]!]
    // Qualquer pixel visível que não seja o vermelho pedido = glifo colorido.
    if (a > 0 && !(r > 200 && g < 60 && b < 60)) return true
  }
  return false
}

export function EmojiPicker({
  anchor,
  onClose,
  onEmoji,
  onGif,
}: {
  anchor: HTMLElement
  onClose: () => void
  onEmoji: (emoji: string) => void
  onGif: (gif: GifDoGiphy) => void
}) {
  const [aba, setAba] = useState<'emoji' | 'gif'>('emoji')

  return (
    <AnchoredOverlay anchor={anchor} onClose={onClose} prefer="acima" gap={22} aria-label="Emojis e GIFs" className="ch-picker">
      {aba === 'emoji' ? <PainelDeEmoji onEmoji={onEmoji} /> : <PainelDeGif onGif={onGif} />}

      <div className="ch-picker__abas" role="tablist" aria-label="Tipo">
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'emoji'}
          className={`ch-kindbtn${aba === 'emoji' ? ' ch-kindbtn--on' : ''}`}
          onClick={() => setAba('emoji')}
        >
          <i className="far fa-smile" aria-hidden /> Emoji
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'gif'}
          className={`ch-kindbtn${aba === 'gif' ? ' ch-kindbtn--on' : ''}`}
          onClick={() => setAba('gif')}
        >
          GIF
        </button>
      </div>
    </AnchoredOverlay>
  )
}

function PainelDeEmoji({ onEmoji }: { onEmoji: (emoji: string) => void }) {
  const [todos, setTodos] = useState<Emoji[] | null>(null)
  const [busca, setBusca] = useState('')
  const [recentes, setRecentes] = useState(lerRecentes)
  const [ativa, setAtiva] = useState(recentes.length > 0 ? 'recentes' : 'rostos')
  const grade = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let vivo = true
    void import('emojibase-data/pt/data.json').then((modulo) => {
      if (!vivo) return
      const lista = (modulo.default as unknown as (Emoji & { group?: number })[])
        .filter((e) => e.group !== undefined && e.group !== 2)
        .filter((e) => e.version <= 12 || sistemaDesenha(e.emoji))
      setTodos(lista)
    })
    return () => {
      vivo = false
    }
  }, [])

  const secoes = useMemo(() => {
    if (!todos) return []
    const termo = busca.trim().toLowerCase()
    if (termo !== '') {
      const achados = todos.filter(
        (e) => e.label.toLowerCase().includes(termo) || e.tags?.some((t) => t.toLowerCase().includes(termo)),
      )
      return [{ id: 'busca', titulo: 'Resultados', emojis: achados.map((e) => e.emoji) }]
    }
    return [
      ...(recentes.length > 0 ? [{ id: 'recentes', titulo: 'Recentes', emojis: recentes }] : []),
      ...CATEGORIAS.map((c) => ({
        id: c.id,
        titulo: c.titulo,
        emojis: todos.filter((e) => c.grupos.includes(e.group)).map((e) => e.emoji),
      })),
    ]
  }, [todos, busca, recentes])

  const irPara = (id: string) => {
    setAtiva(id)
    setBusca('')
    requestAnimationFrame(() => {
      const alvo = grade.current?.querySelector<HTMLElement>(`[data-secao="${id}"]`)
      if (alvo && grade.current) grade.current.scrollTop = alvo.offsetTop - grade.current.offsetTop
    })
  }

  // A aba acompanha a rolagem, como no mensageiro de referência.
  const aoRolar = () => {
    const caixa = grade.current
    if (!caixa || busca.trim() !== '') return
    const topo = caixa.scrollTop + caixa.offsetTop + 8
    let atual = ativa
    for (const el of caixa.querySelectorAll<HTMLElement>('[data-secao]')) {
      if (el.offsetTop <= topo) atual = el.dataset.secao ?? atual
    }
    if (atual !== ativa) setAtiva(atual)
  }

  return (
    <>
      <div className="ch-picker__cats" role="tablist" aria-label="Categorias">
        {[{ id: 'recentes', titulo: 'Recentes', icone: 'far fa-clock' }, ...CATEGORIAS].map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={ativa === c.id}
            aria-label={c.titulo}
            title={c.titulo}
            className={`ch-iconbtn ch-picker__cat${ativa === c.id ? ' ch-picker__cat--on' : ''}`}
            onClick={() => irPara(c.id)}
          >
            <i className={c.icone} aria-hidden />
          </button>
        ))}
      </div>

      <div className="ch-groupsearch ch-picker__busca">
        <i className="fas fa-search" aria-hidden />
        <input
          type="search"
          autoFocus
          value={busca}
          placeholder="Pesquisar emoji"
          aria-label="Pesquisar emoji"
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      <div className="ch-picker__grade" ref={grade} onScroll={aoRolar}>
        {todos === null ? (
          <LoadingState rows={4} label="Carregando emojis" />
        ) : (
          secoes.map((secao) => (
            <section key={secao.id} data-secao={secao.id}>
              <span className="ch-info__rotulo ch-picker__titulo">{secao.titulo}</span>
              {secao.emojis.length === 0 ? (
                <span className="ch-contact__sub ch-picker__vazio">Nenhum emoji com esse nome.</span>
              ) : (
                <div className="ch-picker__emojis">
                  {secao.emojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="ch-picker__emoji"
                      aria-label={emoji}
                      onClick={() => {
                        guardarRecente(emoji)
                        setRecentes(lerRecentes())
                        onEmoji(emoji)
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </section>
          ))
        )}
      </div>
    </>
  )
}

function PainelDeGif({ onGif }: { onGif: (gif: GifDoGiphy) => void }) {
  const [busca, setBusca] = useState('')
  const gifs = useGifs(busca)

  return (
    <>
      <div className="ch-groupsearch ch-picker__busca ch-picker__busca--gif">
        <i className="fas fa-search" aria-hidden />
        <input
          type="search"
          autoFocus
          value={busca}
          placeholder="Pesquisar GIFs no Giphy"
          aria-label="Pesquisar GIFs"
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      <div className="ch-picker__grade">
        {gifs.isPending ? (
          <LoadingState rows={4} label="Carregando GIFs" />
        ) : gifs.isError ? (
          <EmptyState icon="fas fa-exclamation-triangle" title="Giphy indisponível" description="Não foi possível buscar GIFs agora. Tente de novo." />
        ) : !gifs.data?.configurado ? (
          <EmptyState
            icon="fas fa-key"
            title="GIFs ainda não configurados"
            description="Falta a chave da API do Giphy (GIPHY_API_KEY) no servidor."
          />
        ) : gifs.data.gifs.length === 0 ? (
          <EmptyState icon="fas fa-search" title="Nenhum GIF" description="Tente outra palavra." />
        ) : (
          <>
            <span className="ch-info__rotulo ch-picker__titulo">{busca.trim() === '' ? 'Em alta' : 'Resultados'}</span>
            <div className="ch-picker__gifs">
              {gifs.data.gifs.map((gif) => (
                <button key={gif.id} type="button" className="ch-picker__gif" title={gif.titulo} onClick={() => onGif(gif)}>
                  <img src={gif.previa} alt={gif.titulo} loading="lazy" />
                </button>
              ))}
            </div>
            {/* Exigência dos termos do Giphy: a atribuição fica visível. */}
            <span className="ch-contact__sub ch-picker__atribuicao">Powered by GIPHY</span>
          </>
        )}
      </div>
    </>
  )
}
